import { useState } from "react";
import { ArrowUpIcon } from "lucide-react";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Button } from "@ui/button";
import { Textarea } from "@ui/textarea";

export const SadieSimulatorComposer = ({
  isDisabled,
  isSending,
  onSend,
}: {
  isDisabled: boolean;
  isSending: boolean;
  onSend: (body: string) => Promise<void>;
}) => {
  /* React Hooks */
  const [draft, setDraft] = useState("");

  /* Helpers */
  const isSendDisabled = isDisabled || isSending || draft.trim().length === 0;

  /* Handlers */
  // Cleared only once the text went through, so a failed send keeps it to
  // try again. Anything typed while it was sending stays too.
  const handleSubmit = async () => {
    const body = draft.trim();
    if (!body || isSendDisabled) return;
    try {
      await onSend(body);
    } catch {
      return;
    }
    setDraft((current) => (current.trim() === body ? "" : current));
  };

  /* Render */
  return (
    <Column className="mx-auto w-full max-w-2xl gap-2 px-6 pb-4">
      <Column className="rounded-2xl border border-slate-200 bg-white shadow-sm transition-colors focus-within:border-slate-300">
        <Textarea
          autoExpand
          className="max-h-40 min-h-0 resize-none rounded-none border-0 bg-transparent px-4 pb-1 pt-3.5 text-[15px] leading-relaxed shadow-none placeholder:text-slate-400 md:text-[15px]"
          disabled={isDisabled}
          onChange={(e) => {
            setDraft(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void handleSubmit();
            }
          }}
          placeholder={
            isDisabled ? "Start over to text as the tester" : "Text Sadie"
          }
          rows={1}
          value={draft}
        />
        <Row className="items-center justify-between px-3 pb-2.5">
          <Text className="pl-1 text-xs text-slate-400">
            Enter to send. Shift + Enter for a new line.
          </Text>
          <Button
            aria-label="Send"
            className="h-8 w-8 rounded-full"
            disabled={isSendDisabled}
            onClick={() => {
              void handleSubmit();
            }}
            size="icon"
          >
            <ArrowUpIcon className="h-4 w-4" />
          </Button>
        </Row>
      </Column>
      <Text className="text-center text-xs text-slate-400">
        Runs the same code a tester&apos;s texts do. Nothing reaches their
        phone.
      </Text>
    </Column>
  );
};
